import { useState } from "react"
import { MapPin, Briefcase, ChevronRight, Calendar } from "lucide-react"
import { motion } from "framer-motion"

// Utility function included directly in the file
function cn(...classes) {
  return classes.filter(Boolean).join(" ")
}

// Premium job card component with enhanced visual design
const JobCard = ({ job }) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <motion.div
      className={cn(
        "relative overflow-hidden bg-white rounded-xl border border-gray-100",
        "transition-all duration-300 ease-in-out",
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{
        y: -5,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div className="p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 hidden sm:block">
            <div
              className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-xl",
                "bg-[#A2B8D8]",
              )}
            >
              {job.title.charAt(0)}
            </div>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-500">{job.company}</span>
              {job.isRemote && (
                <span className="bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  Remote
                </span>
              )}
              {job.isUrgent && (
                <span className="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">Urgent</span>
              )}
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
              {job.title}
            </h2>

            <p className="text-gray-600 mb-6 line-clamp-2 leading-relaxed">{job.description}</p>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Briefcase className="w-4 h-4 text-gray-400" />
              <span>{job.type}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          "flex gap-3 p-4 sm:p-6 border-t border-gray-100 bg-gray-50 transition-all duration-300",
          isHovered ? "bg-gray-100" : "",
        )}
      >
        <motion.a
          href={job.applyLink}
          className={cn(
            "px-5 py-2.5 rounded-lg font-medium text-sm transition-all",
            "bg-[#143970] text-white shadow-sm",
            "hover:shadow-[#113262]/20 hover:shadow-lg",
          )}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Apply Now
        </motion.a>
        <motion.a
          href={job.viewLink}
          className={cn(
            "px-5 py-2.5 rounded-lg font-medium text-sm transition-all",
            "bg-white text-gray-700 border border-gray-200",
            "hover:bg-gray-50 hover:border-gray-300 flex items-center",
          )}
          whileHover={{ scale: 1.02, x: 3 }}
          whileTap={{ scale: 0.98 }}
        >
          View Details
          <ChevronRight className="w-4 h-4 ml-1 transition-transform" />
        </motion.a>
      </div>
    </motion.div>
  )
}

// Enhanced job listing component
const JobPositions = () => {
  const jobs = [
    {
      title: "Sales & Business Development",
      company: "Maxiom Wealth",
      description:
        "A career where your ambition meets opportunity in the realm of Sales & Business Development, Wealth Management, and Relationship Management. Your role is pivotal—balancing strategic business development with the art of client service.",
      type: "Full-time/Internship",
      isRemote: false,
      isUrgent: false,
      applyLink: "https://mail.google.com/mail/?view=cm&to=hr@maxiomwealth.com",
      viewLink: "/careers/sales",
    },
    {
      title: "Technology and Platform Development",
      company: "Maxiom Wealth",
      description:
        "The cutting-edge world of Technology and Platform Development at Maxiom Wealth involves skills in Python, Django, React Native, and React Native Web. You will be the catalyst for innovation, driving our proprietary technology forward.",
      type: "Full-time/Internship",
      isRemote: false,
      isUrgent: true,
      applyLink: "https://mail.google.com/mail/?view=cm&to=hr@maxiomwealth.com",
      viewLink: "/careers/dev",
    },
    {
      title: "Operations and Client Support",
      company: "Maxiom Wealth",
      description:
        "The cutting-edge world of Technology and Platform Development at Maxiom Wealth involves skills in Python, Django, React Native, and React Native Web. You will be the catalyst for innovation, driving our proprietary technology forward.",
      type: "Full-time/Internship",
      isRemote: false,
      isUrgent: false,
      applyLink: "https://mail.google.com/mail/?view=cm&to=hr@maxiomwealth.com",
      viewLink: "/careers/ops",
    }
  ]

  return (
    <div id="positions" className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 px-4">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        <div className="text-center mb-12">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">Openings at Maxiom</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
          As we continue to shape the future of wealth management, we're looking for visionaries and trailblazers to join our ranks. Your career is more than a job—it's a partnership in progress, a chance to influence an industry, and an opportunity to impact lives.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-1 lg:grid-cols-2">
          {jobs.map((job, index) => (
            <JobCard key={index} job={job} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default JobPositions
